import { useFinancialsAvailability } from "components/financials/modern/data/hooks";
import classNames from "components/ui/classNames";
import { PrimitiveSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from "components/ui/Select";
import getYear from "date-fns/getYear";
import isSameDay from "date-fns/isSameDay";
import isSameYear from "date-fns/isSameYear";
import max from "date-fns/max";
import min from "date-fns/min";
import uniqBy from "lodash/uniqBy";
import { useState } from "react";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import type { IIndicatorDataSource, ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType, IPlatformVertical } from "types/graphqlTypes";

interface ReportDatesRangeProps {
  loading: boolean;
  tickerSymbol: string;
  country: ISecurityCountry;
  vertical: IPlatformVertical;
  statementType: IFinancialsStatementType;
  interim?: string | null;
  reportDates: Date[];
  indicatorDataSource: IIndicatorDataSource;
  disabled?: boolean;
  value?: [Date | null, Date | null];
  onChange?: (value: [Date, Date]) => void;
}

export default function ReportDatesRange({
  loading,
  tickerSymbol,
  country,
  statementType,
  interim,
  reportDates,
  disabled,
  vertical,
  indicatorDataSource,
  value,
  onChange,
}: ReportDatesRangeProps) {
  const intl = useIntl();
  const [limit, setLimit] = useState("TEN_RECORDS");

  const { loading: isFinancialsAvailabilityLoading, reportsDatesByStatementType } = useFinancialsAvailability(
    tickerSymbol,
    country,
    vertical,
    indicatorDataSource,
  );

  const limits = getLimits(intl);
  const currentLimitValue = limits.find((node) => node.value === limit)?.records || 10;
  const adjustedLimit = adjustLimit(currentLimitValue, statementType, interim);

  const allYears = uniqBy(reportsDatesByStatementType[statementType], (date) => getYear(date));

  const minDate = min(reportsDatesByStatementType[statementType]);
  const maxDate = max(reportsDatesByStatementType[statementType]);

  const maxDateIndex = reportsDatesByStatementType[statementType].findIndex((date) => isSameDay(date, maxDate));
  const candidateMinDate =
    reportsDatesByStatementType[statementType]
      .slice(Math.max(maxDateIndex - (adjustedLimit - 1), 0), maxDateIndex)
      .at(0) || minDate;

  let range = [candidateMinDate, maxDate];

  const isFiis = vertical === IPlatformVertical.Fiis;

  if (isFiis && (!value || !value[0] || !value[1])) {
    const lastTwelveMonths = reportsDatesByStatementType[statementType]
      .slice(-12)
      .sort((a, b) => a.getTime() - b.getTime());
    const startDate = lastTwelveMonths[0] || minDate;
    const endDate = lastTwelveMonths.at(-1) || maxDate;

    range = [startDate, endDate];
  }

  if (isFinancialsAvailabilityLoading || (loading && reportDates.length === 0)) {
    return (
      <div className="flex w-full gap-2">
        <div
          aria-hidden={true}
          className={classNames(
            "relative flex h-9 w-32 animate-pulse items-center justify-center rounded-lg bg-slate-200",
            "dark:bg-slate-700 dark:text-slate-300",
            "cursor-not-allowed select-none",
          )}
        />
        {!isFiis && (
          <>
            <div
              aria-hidden={true}
              className={classNames(
                "relative flex h-9 w-32 animate-pulse items-center justify-center rounded-lg bg-slate-200",
                "dark:bg-slate-700 dark:text-slate-300",
                "cursor-not-allowed select-none",
              )}
            />
            <div
              aria-hidden={true}
              className={classNames(
                "relative flex h-9 w-12 animate-pulse items-center justify-center rounded-lg bg-slate-200",
                "dark:bg-slate-700 dark:text-slate-300",
                "cursor-not-allowed select-none",
              )}
            />
          </>
        )}
      </div>
    );
  }

  if (reportDates.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full items-center gap-2">
      <div className="flex w-full">
        <Select
          disabled={disabled}
          value={getSelectedStartValue(
            value,
            range,
            isFiis,
            statementType,
            reportsDatesByStatementType,
            minDate,
            maxDate,
          )}
          onValueChange={(selectedYear) => {
            const values = handleOnStartChange(
              selectedYear,
              value,
              range,
              statementType,
              reportsDatesByStatementType,
              minDate,
              maxDate,
              adjustedLimit,
              allYears,
              isFiis,
            );

            onChange && onChange(values as [Date, Date]);
          }}
        >
          <SelectTrigger
            className={classNames(
              "inline-flex",
              "w-full min-w-28 border-none border-transparent bg-white xl:max-w-32",
              "font-aeonik text-sm font-medium tracking-wide text-slate-600",
              "hover:bg-slate-50 dark:hover:bg-slate-600",
              "ring-1 ring-inset ring-slate-200",
              "dark:ring-slate-950",
              "focus-visible:border-transparent focus-visible:ring-slate-100 focus-visible:ring-offset-slate-300",
              "disabled:select-none",
              "truncate",
              "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
              "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
              "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
              "rounded-xl",
              "dark:focus:ring-slate-900 dark:focus-visible:ring-slate-900",
            )}
          >
            <SelectValue
              className="text-xs font-bold tracking-wide"
              placeholder={intl.formatMessage({
                defaultMessage: "Início",
                id: "m42V4+",
              })}
            />
          </SelectTrigger>
          <SelectContent className="max-h-64 rounded-lg border border-slate-200 text-sm">
            {allYears
              .filter((date) => !isSameYear(date, maxDate))
              .map((date) => (
                <PrimitiveSelectItem
                  key={`start/${date.getTime()}`}
                  value={getYear(date).toString()}
                  className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
                >
                  <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                    <h4 className="inline-flex text-xs dark:text-slate-300">{getYear(date)}</h4>
                  </div>
                </PrimitiveSelectItem>
              ))}

            {isFiis && (
              <PrimitiveSelectItem
                value="TTM"
                className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
              >
                <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                  <h4 className="inline-flex text-xs dark:text-slate-300">
                    <FormattedMessage defaultMessage="Últimos 12 meses" id="mQUosq" />
                  </h4>
                </div>
              </PrimitiveSelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      {!isFiis && (
        <>
          <div className="flex w-full">
            <Select
              disabled={disabled}
              value={value && value[1] ? getYear(value[1]).toString() : getYear(range[1]).toString()}
              onValueChange={(selectedYear) => {
                const filterByDates = reportsDatesByStatementType[statementType].filter(
                  (reportDate) => getYear(reportDate).toString() === selectedYear,
                );
                const filterByDate = max(filterByDates);

                const currentEndDate = value && value[1] ? value[1] : range[1];
                const endDate = filterByDate || currentEndDate;
                const endDateIndex = reportsDatesByStatementType[statementType].findIndex((date) =>
                  isSameDay(date, endDate),
                );

                const startDate =
                  reportsDatesByStatementType[statementType]
                    .slice(Math.max(endDateIndex - (adjustedLimit - 1), 0), endDateIndex)
                    .at(0) || minDate;

                onChange && onChange([startDate, endDate]);
              }}
            >
              <SelectTrigger
                className={classNames(
                  "inline-flex",
                  "w-full min-w-28 border-none border-transparent bg-white xl:max-w-32",
                  "font-aeonik text-sm font-medium tracking-wide text-slate-600",
                  "hover:bg-slate-50 dark:hover:bg-slate-600",
                  "ring-1 ring-inset ring-slate-200",
                  "dark:ring-slate-950",
                  "focus-visible:border-transparent focus-visible:ring-slate-100 focus-visible:ring-offset-slate-300",
                  "disabled:select-none",
                  "truncate",
                  "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                  "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
                  "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
                  "rounded-xl",
                  "dark:focus:ring-slate-900 dark:focus-visible:ring-slate-900",
                )}
              >
                <SelectValue
                  className="text-xs font-bold tracking-wide"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Fim",
                    id: "tVXNb3",
                  })}
                />
              </SelectTrigger>
              <SelectContent className="max-h-64 rounded-lg border border-slate-200 text-sm">
                {allYears
                  .filter((date) => !isSameYear(date, minDate))
                  .map((date) => (
                    <PrimitiveSelectItem
                      key={`end/${date.getTime()}`}
                      value={getYear(date).toString()}
                      className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
                    >
                      <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                        <h4 className="inline-flex text-xs dark:text-slate-300"> {getYear(date)}</h4>
                      </div>
                    </PrimitiveSelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex">
            <Select
              disabled={disabled}
              value={limit}
              onValueChange={(limit) => {
                setLimit(limit);

                const limitCandidate = adjustLimit(
                  limits.find((node) => node.value === limit)?.records || 10,
                  statementType,
                  interim,
                );
                const endDate = value && value[1] ? value[1] : range[1];
                const endDateIndex = reportsDatesByStatementType[statementType].findIndex((date) => {
                  return isSameDay(date, endDate);
                });

                const startDate =
                  reportsDatesByStatementType[statementType]
                    .slice(Math.max(endDateIndex - (limitCandidate - 1), 0), endDateIndex)
                    .at(0) || minDate;
                onChange && onChange([startDate, endDate]);
              }}
            >
              <SelectTrigger
                hideChevronIcon={true}
                className={classNames(
                  "inline-flex",
                  "w-full border-none border-transparent bg-white p-1",
                  "font-aeonik text-sm font-medium tracking-wide text-slate-600",
                  "hover:bg-slate-50 dark:hover:bg-slate-600",
                  "ring-1 ring-inset ring-slate-200",
                  "dark:ring-slate-950",
                  "focus-visible:border-transparent focus-visible:ring-slate-100 focus-visible:ring-offset-slate-300",
                  "disabled:select-none",
                  "truncate",
                  "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                  "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
                  "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
                  "rounded-xl",
                  "dark:focus:ring-slate-900 dark:focus-visible:ring-slate-900",
                )}
              >
                <div
                  className={classNames(
                    "flex w-full items-center justify-between",
                    "rounded-lg px-3 py-1.5",
                    "bg-sky-100 text-sky-700",
                    "dark:bg-sky-700/40 dark:text-sky-400",
                  )}
                >
                  {currentLimitValue}
                </div>
              </SelectTrigger>
              <SelectContent className="rounded-lg border border-slate-200 text-sm" side="bottom">
                {limits.map(({ label, value }) => (
                  <PrimitiveSelectItem
                    key={label}
                    value={value}
                    className="data-[state=checked]:bg-slate-100 hover:data-[state=checked]:bg-slate-600/10 dark:data-[state=checked]:bg-slate-950"
                  >
                    <div className="flex w-full cursor-pointer items-center justify-between rounded-xl py-1 text-sm">
                      <h4 className="inline-flex text-xs dark:text-slate-300">{label}</h4>
                    </div>
                  </PrimitiveSelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </div>
  );
}

function adjustLimit(limit: number, statementType: IFinancialsStatementType, interim?: string | null) {
  if (statementType === IFinancialsStatementType.Quarterly && interim) {
    return limit * 4;
  }

  return limit;
}

function getLimits(intl: IntlShape) {
  return [
    {
      label: intl.formatMessage({ defaultMessage: "5 balanços", id: "Da95HF" }),
      value: "FIVE_RECORDS",
      records: 5,
    },
    {
      label: intl.formatMessage({ defaultMessage: "10 balanços", id: "ra1M+e" }),
      value: "TEN_RECORDS",
      records: 10,
    },
    {
      label: intl.formatMessage({ defaultMessage: "15 balanços", id: "oGB7zO" }),
      value: "FIFTEEN_RECORDS",
      records: 15,
    },
    {
      label: intl.formatMessage({ defaultMessage: "20 balanços", id: "XV56Ug" }),
      value: "TWENTY_RECORDS",
      records: 20,
    },
    {
      label: intl.formatMessage({ defaultMessage: "30 balanços", id: "Deak7Y" }),
      value: "THIRTY_RECORDS",
      records: 30,
    },
  ];
}

export function getSafeFilterByReportDates(value?: [Date | null, Date | null]): [Date, Date] | null {
  if (value && value[0] && value[1]) {
    return value[0].getTime() > value[1].getTime() ? [value[1], value[0]] : [value[0], value[1]];
  }
  return null;
}
function getSelectedStartValue(
  value: [Date | null, Date | null] | undefined,
  range: Date[],
  isFiis: boolean,
  statementType: string,
  reportsDatesByStatementType: Record<string, Date[]>,
  minDate: Date,
  maxDate: Date,
): string {
  const getLastTwelveMonthsRange = () => {
    const dates = reportsDatesByStatementType[statementType].slice(-12).sort((a, b) => a.getTime() - b.getTime());

    return {
      start: dates[0] || minDate,
      end: dates.at(-1) || maxDate,
    };
  };

  const startValueDate = value && value[0];
  const endValueDate = value && value[1];

  const dateRange = startValueDate && endValueDate ? [startValueDate, endValueDate] : range;

  if (!dateRange[0]) return "";

  if (!isFiis) {
    return getYear(dateRange[0]).toString();
  }

  const { start, end } = getLastTwelveMonthsRange();

  const isTTM = isSameDay(dateRange[0], start) && isSameDay(dateRange[1], end);
  return isTTM ? "TTM" : getYear(dateRange[0]).toString();
}

function handleOnStartChange(
  selectedYear: string,
  value: [Date | null, Date | null] | undefined,
  range: Date[],
  statementType: string,
  reportsDatesByStatementType: Record<string, Date[]>,
  minDate: Date,
  maxDate: Date,
  adjustedLimit: number,
  allYears: Date[],
  isFiis: boolean,
) {
  const isTTM = selectedYear === "TTM";

  if (isTTM) {
    const lastTwelveMonths = reportsDatesByStatementType[statementType]
      .slice(-12)
      .sort((a, b) => a.getTime() - b.getTime());
    const startDate = lastTwelveMonths[0] || minDate;
    const endDate = lastTwelveMonths.at(-1) || maxDate;

    return [startDate, endDate];
  }

  if (isFiis) {
    const filterByDates = reportsDatesByStatementType[statementType].filter(
      (reportDate) => getYear(reportDate).toString() === selectedYear,
    );
    const startDate = min(filterByDates);
    const endDate = max(filterByDates);

    return [startDate, endDate];
  }

  const filterByDate = allYears.find((reportDate) => getYear(reportDate).toString() === selectedYear);

  const currentStartDate = value && value[0] ? value[0] : range[0];
  const startDate = filterByDate || currentStartDate;
  const startDateIndex = reportsDatesByStatementType[statementType].findIndex((date) => isSameDay(date, startDate));

  const endDate =
    reportsDatesByStatementType[statementType].slice(startDateIndex, startDateIndex + adjustedLimit).at(-1) || maxDate;

  return [startDate, endDate];
}
